// Pure selection/sorting helpers extracted from the Dashboard and ClientDetail
// pages so the logic is unit-testable without rendering. Behavior-preserving:
// each function is the exact body of the useMemo/derivation it replaced.

import { isHiddenUserEmail } from '../constants/excludedUsers';
import { ClientSummary, ClientUser, RangeRep } from '../types/superadmin';

export interface FlagFilterOption {
  id: string;
  label: string;
}

export interface FlagFilters {
  filterOptions: FlagFilterOption[];
  filterCounts: Record<string, number>;
}

/** Distinct risk-flag keys across clients, with labels and per-key counts. */
export function collectFlagFilters(clients: ClientSummary[]): FlagFilters {
  const options: Record<string, string> = {};
  const counts: Record<string, number> = {};
  clients.forEach((client) => {
    client.flags.forEach((riskFlag) => {
      options[riskFlag.key] = riskFlag.label;
      counts[riskFlag.key] = (counts[riskFlag.key] || 0) + 1;
    });
  });
  return {
    filterOptions: Object.entries(options).map(([id, label]) => ({ id, label })),
    filterCounts: counts
  };
}

/** Clients carrying at least one selected flag; every client when none selected. */
export function filterClientsByFlags(
  clients: ClientSummary[],
  selectedKeys: Set<string>
): ClientSummary[] {
  if (selectedKeys.size === 0) return clients;
  return clients.filter((client) => client.flags.some((f) => selectedKeys.has(f.key)));
}

export type NameSortDir = 'asc' | 'desc' | null;

/** Alphabetical resort; null keeps the backend's risk order untouched. */
export function sortClientsByName(clients: ClientSummary[], dir: NameSortDir): ClientSummary[] {
  if (!dir) return clients;
  return [...clients].sort((a, b) => {
    const cmp = a.displayName.localeCompare(b.displayName, undefined, { sensitivity: 'base' });
    return dir === 'desc' ? -cmp : cmp;
  });
}

export type RepSortKey =
  | 'callQualityScore'
  | 'trainingQualityScore'
  | 'trainingCompletion'
  | 'sessions'
  | 'minutes'
  | 'objFaqScore'
  | 'landmineScore'
  | 'competitorScore'
  | 'playbookScore';

/** Sort reps by a numeric column; null scores always sink to the bottom. */
export function sortRepsBy<T extends RangeRep>(reps: T[], key: RepSortKey, dir: 'asc' | 'desc'): T[] {
  return [...reps].sort((a, b) => {
    const av = a[key];
    const bv = b[key];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    return dir === 'desc' ? bv - av : av - bv;
  });
}

/** Reps with any call or training activity in the selected range. */
export function countActiveReps(reps: RangeRep[]): number {
  return reps.filter((rep) => rep.sessions > 0 || rep.trainingSessions > 0).length;
}

/** One row of the client page's people table: a live user joined to their range stats. */
export type ClientPersonRow = RangeRep & {
  /** Live account; null when the rep has range activity but no account any more. */
  user: ClientUser | null;
};

const emptyRepFor = (user: ClientUser, tenantId: string): RangeRep => ({
  userId: user.firebaseUid,
  tenantId,
  displayName: '',
  name: user.name,
  email: user.email,
  sessions: 0,
  minutes: 0,
  objFaqScore: null,
  landmineScore: null,
  competitorScore: null,
  playbookScore: null,
  callQualityScore: null,
  trainingQualityScore: null,
  trainingCompletion: null,
  trainingSessions: 0
});

/**
 * Join the live roster to the range stats on Firebase UID (backend-tenant
 * keys reps by `firebaseUid`). Every live user gets a row, with empty
 * stats when they had no activity; reps with stats but no live account (removed
 * since) still get a row so their history stays visible. Hidden staff accounts
 * are dropped from both sides.
 */
export function mergeUsersWithReps(users: ClientUser[], reps: RangeRep[], tenantId: string): ClientPersonRow[] {
  const visibleUsers = users.filter((user) => !isHiddenUserEmail(user?.email));
  const repsByUid = new Map(reps.map((rep) => [rep.userId, rep]));
  const liveUids = new Set(visibleUsers.map((user) => user.firebaseUid));

  const liveRows = visibleUsers.map((user) => ({
    ...(repsByUid.get(user.firebaseUid) ?? emptyRepFor(user, tenantId)),
    // The live account is the source of truth for who someone is.
    name: user.name,
    email: user.email,
    user
  }));
  const removedRows = reps
    .filter((rep) => !liveUids.has(rep.userId) && !isHiddenUserEmail(rep?.email))
    .map((rep) => ({ ...rep, user: null }));
  return [...liveRows, ...removedRows];
}
